export { defineTool } from "./types.js";

export type {
  AgentHarness,
  HarnessEvent,
  HarnessSystemEvent,
  HarnessSystemInitEvent,
  HarnessCompactBoundaryEvent,
  HarnessTaskStartedEvent,
  HarnessTaskProgressEvent,
  HarnessAssistantEvent,
  HarnessContentBlock,
  HarnessResultEvent,
  HarnessResultSuccessEvent,
  HarnessResultErrorEvent,
  HarnessRateLimitEvent,
  HarnessToolProgressEvent,
  HarnessUserQuestion,
  HarnessUserQuestionEvent,
  HarnessUserMessage,
  HarnessToolDefinition,
  HarnessToolAnnotations,
  HarnessMcpServer,
  HarnessQueryOptions,
  HarnessHookInput,
  HarnessHookOutput,
  HarnessPostToolUseHook,
  PtyBridge,
} from "./types.js";

export { ClaudeCodeHarness } from "./claude-code/index.js";
export { PtyHarness } from "./pty/index.js";
export { OpenCodeHeadlessHarness } from "./opencode/index.js";

import { ClaudeCodeHarness } from "./claude-code/index.js";
import { PtyHarness } from "./pty/index.js";
import { OpenCodeHeadlessHarness } from "./opencode/index.js";
import type { TuiAdapter } from "./pty/adapters/index.js";
import type { AgentHarness, PtyBridge } from "./types.js";

/**
 * Which harness drives a run.
 *
 * - `"sdk"`   — ClaudeCodeHarness, the in-process Claude Agent SDK loop.
 * - `"pty"`   — PtyHarness, an interactive TUI relayed to the Connected-TUI tab.
 *   Opencode cards run here too: the OpenCodeTuiAdapter has a trusted
 *   structured-event source (the Conveyor events plugin — see
 *   harness/opencode/plugin.ts), so the card gets chat mirroring, MCP tools
 *   and real turn completion while the TUI stays watchable and typable.
 * - `"opencode"` — OpenCodeHeadlessHarness, `opencode run --format json`.
 *   UNROUTED since the PTY adapter gained structured events; kept in-tree as
 *   the fallback if the TUI path hits pod trouble (it was verified live in
 *   PR #3893 and its tests still pin it).
 */
export type HarnessKind = "sdk" | "pty" | "opencode";

/**
 * Can this harness accept multimodal image blocks in a prompt?
 *
 * Only the SDK can. A PTY harness would paste the blocks into the TUI as text,
 * and the headless opencode harness passes its prompt as argv — so both get a
 * text-only prompt whose body links each image via the attachment tools.
 */
export function supportsImageBlocks(kind: HarnessKind): boolean {
  return kind === "sdk";
}

/**
 * Pick the harness implementation. `ptyBridge` is only consumed by the PTY
 * harness (to stream stdout to the S2 relay and receive keystrokes/resize);
 * the SDK and headless harnesses ignore it.
 *
 * `adapter` selects WHICH TUI the PTY harness drives (claude / opencode).
 * Omitting it keeps `PtyHarness`'s own ClaudeTuiAdapter default, so the no-arg
 * and `("pty", bridge)` call shapes are byte-for-byte unchanged — the factory
 * default stays "sdk" + Claude, which the harness-selection guards lock.
 */
export function createHarness(
  kind: HarnessKind = "sdk",
  ptyBridge?: PtyBridge,
  adapter?: TuiAdapter,
): AgentHarness {
  if (kind === "opencode") return new OpenCodeHeadlessHarness();
  if (kind !== "pty") return new ClaudeCodeHarness();
  return adapter ? new PtyHarness(ptyBridge, adapter) : new PtyHarness(ptyBridge);
}
